import React, { ReactNode } from "react";
import {
    Button, Card, Col, Container, InputGroup,
    OverlayTrigger, Row, Tooltip, Form, Alert,
    Modal, Collapse
} from "react-bootstrap";
import {
    Copy as CopyIcon,
    InfoCircle as InfoIcon,
    Pencil as EditIcon,
    Trash as DeleteIcon,
    Lock as LockIcon,
    Unlock as UnlockIcon
} from "react-bootstrap-icons";
import styled from "styled-components";
import "rc-slider/assets/index.css";
import { mkConfig, generateCsv, download } from "export-to-csv";
import moment from "moment";
import { RouteComponentProps, withRouter } from "react-router-dom";

import * as constants from "../constants";
import { deleteModelTopic, getHealthDomainTopicInformation, getHostURL, getModelDomainInformation, getOperationalSlotInfo, getRecFilterDomainInformation, makeSlotRequest, postModelDomainInformation } from "../utils/Requests";
import RandomField from "../components/fields/RandomField";
import { extractParameterValue, isReadOnly as isReadOnlyUser } from "../utils/Utils";
import MetadataCarousel from "../components/MetadataCarousel";
import copy from "copy-to-clipboard";
import { Typeahead } from "react-bootstrap-typeahead";
import Branding from "../config/Branding";
import { CarouselRow, CentralCol, FieldValue } from "../components/SharedStyled";
import ModelTypeModal from "../components/models/ModelTypeModal";

const ParametersAlert = styled(Alert)`
    position: absolute;
    top: 75px;
    right: 15px;
    z-index: 10;
    min-width: 200px;
    height: 42px;
`;

const MLTRow = styled(Row)`
    background-color: ${Branding.superDarkBackground};
    border-radius: 3px;
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 5px 5px;
    width: 100%;
`;

const SelectedEntityCol = styled(Col)`
    text-align: center;
    padding: 0;
`;

function getModelTypeColor(modelType: string): string {
    switch (modelType) {
        case "dynamic":                    return "#7e51ff";  // violet
        case "mlt": case "mlt_audio":     return "#17a2b8";  // teal
        case "rfy": case "rfy_audio":     return "#28a745";  // green
        case "byw": case "byw_audio":     return "#ffc107";  // amber
        case "chart":                      return "#fd7e14";  // orange
        case "most_popular":               return "#e83e8c";  // pink
        case "latest":                     return "#20c997";  // mint
        case "leaving_soon":               return "#dc3545";  // red
        case "trending":                   return "#ff6b35";  // coral
        case "hero":                       return "#3d9be9";  // sky blue
        case "browse":                     return "#6610f2";  // indigo
        case "similar":                    return "#b6ff00";  // lime
        case "search":                     return "#868e96";  // slate grey
        default:                           return "#6c757d";  // grey
    }
}


const ModelGroupHeader = styled.div<{ $accentcolor: string }>`
    font-size: 0.78em;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #ccc;
    padding: 12px 0 6px 0;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
        content: "";
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: ${p => p.$accentcolor};
        flex-shrink: 0;
    }
`;

const ModelRow = styled(Card)`
    background-color: ${Branding.darkBackground};
    color: #fff;
    margin-bottom: 4px;
    border: 1px solid rgba(255,255,255,0.07);
`;

const ModelGrid = styled.div`
    display: flex;
    align-items: stretch;
`;

const ModelGridAccent = styled.div<{ $accentcolor: string }>`
    width: 3px;
    flex-shrink: 0;
    background-color: ${(p: any) => p.$accentcolor};
    border-radius: 2px 0 0 2px;
`;

// Fixed columns: Name | Type | Slot | Owner | Updated By | Created | Updated | Status | Active | 7d | 
const MODEL_COLS = "360px 90px 240px 120px 120px 60px 60px 60px 60px 120px";

const ModelGridBody = styled.div`
    display: grid;
    grid-template-columns: ${MODEL_COLS};
    gap: 0 10px;
    padding: 6px 10px;
    font-size: 0.75em;
    align-items: center;
    min-width: 0;
`;

const ModelGridCell = styled.div`
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #fff;
    line-height: 1.6;
`;

const ModelUrlRow = styled.div`
    padding: 0 10px 6px 10px;
    font-family: monospace;
    font-size: 0.72em;
    color: #aaa;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ModelNamePill = styled.span`
    font-weight: 600;
    font-size: 1em;
    color: #fff;
    background-color: rgba(255,255,255,0.08);
    padding: 2px 8px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
`;

const ModelTableHeader = styled.div`
    display: flex;
    padding: 0 0 4px 0;
`;

const ModelTableHeaderBody = styled.div`
    display: grid;
    grid-template-columns: ${MODEL_COLS};
    gap: 0 10px;
    padding: 0 10px 0 13px;
    font-size: 0.72em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #888;
`;

const ModelGridActions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 6px;
    flex-shrink: 0;
`;

const StickyToolbar = styled.div`
    position: sticky;
    top: 62px;
    z-index: 100;
    background: ${Branding.superDarkBackground};
    padding-bottom: 4px;
    padding-top: 4px;
`;

type ModelManagerState = {
    availableModelInfo: Array<Record<string, any>>,
    showSuccess: boolean,
    showDelete: boolean,
    showDeleteModal: boolean,
    showModelTypeModal: boolean,
    view: string,
    errorMsg: string,
    selectedPlaylistName: string,
    selectedPlaylistStatus: string,
    selectedPlaylistModel: string,
    selectedPlaylistVariant: string,
    selectedPlaylistOverlay: string,
    selectedPlaylistURL: string,
    selectedPlaylistDeletable: boolean,
    deletePlaylistName: string,
    deleteModelId: string,
    deleteModelType: string,
    userId: string,
    seedId: string,
    slotsInfo: Record<string, any>,
    availableVariants: Record<string,any>,
    availableOverlays: Record<string,any>,
    availableFilters: Record<string,any>,
    items: Array<Record<string,any>>,
    filters: Record<string, Array<Record<string, string>>>,
    usageByModel: Record<string, Record<string, any>>,
    sortBy: string,
    sortOrder: "asc" | "desc",
    searchQuery: string,
    showAdvancedFilters: boolean,
    filterStatus: string,
    filterOwner: string,
    filterSlotType: string,
    filterUsedIn7d: string,
    conditionalFilterGroups: Array<{ topic: string; values: string[] }>
}

type ModelManagerProperties = RouteComponentProps & {
    standalone?: boolean,
    groups: string[],
    username: string,
    customerConfig: CustomerConfig
}

class ModelManager extends React.Component<ModelManagerProperties, ModelManagerState> {

    private OVERVIEW = "Overview";
    private NEW = "New";
    private EDIT = "Edit";

    private availableStatus = ["Live", "Test", "Draft", "24iQ Only"];
    private filterableModels = ["chart", "latest", "most_popular", "leaving_soon"]

    constructor(props: ModelManagerProperties) {
        super(props);

        this.state = {
            availableModelInfo: [],
            showSuccess: false,
            showDelete: false,
            showDeleteModal: false,
            showModelTypeModal: false,
            view: this.OVERVIEW,
            errorMsg: "",
            selectedPlaylistName: "",
            selectedPlaylistStatus: "Live",
            selectedPlaylistModel: "",
            selectedPlaylistVariant: "",
            selectedPlaylistOverlay: "",
            selectedPlaylistURL: "",
            selectedPlaylistDeletable: false,
            deletePlaylistName: "",
            deleteModelId: "",
            deleteModelType: "",
            userId: "cold",
            seedId: "",
            slotsInfo: {},
            availableVariants: {},
            availableOverlays: {},
            availableFilters: {},
            items: [],
            filters: {},
            usageByModel: {},
            sortBy: "model_type",
            sortOrder: "asc",
            searchQuery: "",
            showAdvancedFilters: false,
            filterStatus: "",
            filterOwner: "",
            filterSlotType: "",
            filterUsedIn7d: "",
            conditionalFilterGroups: []
        };
    }

    public componentDidMount(): void {
        this.reset();
    }

    private toggleSelectedPlaylistDeletable = () => {
        this.setState((state) => ({
            selectedPlaylistDeletable: !state.selectedPlaylistDeletable
        }));
    };

    private reset(): void {
        this.requestModelDomainInfo();
        this.requestCustomerSlotInfo();
        this.getRecFilterInfo();
        this.requestPersonalisationUsage();
    }

    private async requestModelDomainInfo(): Promise<void> {
        await getModelDomainInformation(this.props.customerConfig).then((response: Array<Record<string, any>>) => {
            const isFullAccess = this.props.groups.includes(constants.THE_FILTER_FULL_AUTH);
            const filtered = isFullAccess
                ? response
                : response.filter((model: Record<string, any>) => model.status !== "24iQ Only");
            this.setState({
                availableModelInfo: filtered
            });
        });
    }

    private async requestPersonalisationUsage(): Promise<void> {
        try {
            const response = await getHealthDomainTopicInformation(this.props.customerConfig, "events", "personalisation_by_model");
            const usageMap: Record<string, Record<string, any>> = {};
            const records = Array.isArray(response) && response.length > 0 && Array.isArray(response[0]?.value)
                ? response[0].value
                : (Array.isArray(response) ? response : []);

            records.forEach((record: Record<string, any>) => {
                const key = record.model_id || record.slot_id;
                if (key) {
                    usageMap[key] = record;
                }
            });

            this.setState({ usageByModel: usageMap });
        } catch (error) {
            // Usage data is non-critical for model listing.
        }
    }

    private async getRecFilterInfo(): Promise<void> {
        await getRecFilterDomainInformation(this.props.customerConfig).then((response: Array<Record<string, any>>) => {
            const filterState: Record<string, Array<Record<string, string>>> = {};
            const availableFilters: Record<string, any> = {};

            Object.values(response).forEach((filter: Record<string, any>) => {
                filterState[filter.topic] = [];
                availableFilters[filter.topic] = {
                    "qsp": filter.topic,
                    "label": filter.value.label,
                    "values": filter.value.values,
                    "cardinality": filter.value.cardinality
                };
            });

            this.setState({
                availableFilters,
                filters: filterState
            });
        });
    }

    private async requestCustomerSlotInfo(): Promise<void> {
        await getOperationalSlotInfo(this.props.customerConfig.slug).then((response: Record<string, any>) => {
            let slotsInfoResponse: Record<string,any>[] = [];
            const slotsInfo: Record<string, any> = {};

            for (const slotKey in response["slots"]) {
                slotsInfoResponse = slotsInfoResponse.concat(response["slots"][slotKey]);
            }

            slotsInfoResponse.forEach((slot: Record<string, any>) => {
                // Skip Email RFY for EPIX as it doesn't fit this concept.
                if (slot["slotId"] !== "6e0a51f3-5651-4c1c-a2c3-8b6479e64a24") {
                    const variants = slot["parameters"]["variant"];
                    const overlays = slot["parameters"]["overlay"];

                    let variantInfo: Record<string,any>[] = [];
                    if (variants !== undefined) {
                        variantInfo = Object.values(variants["options"]).map((variant: any) => {
                            return {
                                "name": variant["displayName"],
                                "qsp": variant["qsp"]
                            };
                        });
                    }

                    let overlayInfo: Record<string,any>[] = [];
                    if (overlays !== undefined) {
                        overlayInfo = Object.values(overlays["options"]).map((overlay: any) => {
                            return {
                                "name": overlay["displayName"],
                                "qsp": overlay["qsp"]
                            };
                        });

                    }

                    slotsInfo[slot["slotId"]] = {
                        "id": slot["slotId"],
                        "name": slot["slotName"],
                        "type": slot["type"],
                        "userId": Object.keys(slot["parameters"]).includes("userId"),
                        "seedId": Object.keys(slot["parameters"]).includes("seedIds"),
                        "variants": variantInfo,
                        "overlays": overlayInfo
                    };
                }
            });

            this.setState({
                slotsInfo
            });
        });
    }

    private closeSuccess = (): void => {
        this.setState({
            errorMsg: "",
            showSuccess: false
        });
    }

    private closeDelete = (): void => {
        this.setState({
            showDelete: false,
            errorMsg: ""
        });
    }

    private switchToNew = (): void => {
        this.setState({ showModelTypeModal: true });
    }

    private handleClassicModelSelection = (): void => {
        this.setState({ showModelTypeModal: false });
        const firstSlot = Object.values(this.state.slotsInfo)[0];

        this.setState({
            view: this.NEW,
            selectedPlaylistModel: firstSlot["id"],
            selectedPlaylistName: "",
            availableVariants: firstSlot["variants"],
            availableOverlays: firstSlot["overlays"],
            selectedPlaylistVariant: firstSlot["variants"][0]["qsp"],
            selectedPlaylistOverlay: firstSlot["overlays"][0]["qsp"],
            selectedPlaylistStatus: "Live",
            selectedPlaylistDeletable: true
        }, this.setUrl);
    }

    private handleDynamicModelSelection = (): void => {
        this.setState({ showModelTypeModal: false });
        this.props.history.push("/dynamicmodelbuilder");
    }

    private closeModelTypeModal = (): void => {
        this.setState({ showModelTypeModal: false });
    }

    private switchToOverview = (): void => {
        this.setState({
            availableModelInfo: [],
            showSuccess: false,
            view: this.OVERVIEW,
            errorMsg: "",
            selectedPlaylistName: "",
            selectedPlaylistStatus: "",
            selectedPlaylistModel: "",
            selectedPlaylistVariant: "",
            selectedPlaylistOverlay: "",
            selectedPlaylistURL: "",
            selectedPlaylistDeletable: false,
            deletePlaylistName: "",
            deleteModelId: "",
            deleteModelType: "",
            userId: "cold",
            seedId: "",
            slotsInfo: {},
            availableVariants: {},
            availableOverlays: {},
            items: [],
            availableFilters: [],
            filters: {},
            usageByModel: {},
            sortBy: "model_type",
            sortOrder: "asc",
            searchQuery: "",
            showAdvancedFilters: false,
            filterStatus: "",
            filterOwner: "",
            filterSlotType: "",
            filterUsedIn7d: "",
            conditionalFilterGroups: []
        }, this.reset);
    }

    private switchToEdit = (row: Record<string, any>): void => {
        // Check if this is a dynamic model
        if (row["model_type"] === "dynamic") {
            const modelId = row["model_id"] || row["topic"];
            this.props.history.push(`/dynamicmodelbuilder?modelId=${modelId}`);
            return;
        }

        // Existing logic for classic models
        const filterState = this.state.filters;
        Object.keys(this.state.availableFilters).forEach((filterKey) => {
            if (filterKey in row) {
                // Backwards compatability, if required
                if (typeof row[filterKey] === "string") {
                    filterState[filterKey] = [{
                        "value": `${row[filterKey]}`,
                        "label": `${row[filterKey]}`
                    }];
                } else {
                    filterState[filterKey] = row[filterKey];
                }
            }
        });

        const slotInfo = this.state.slotsInfo[row["slot_id"]];
        const availableVariants = slotInfo?.["variants"] || [{ "name": extractParameterValue(row["variant"] || ""), "qsp": row["variant"] || "" }];
        const availableOverlays = slotInfo?.["overlays"] || [{ "name": extractParameterValue(row["overlay"] || ""), "qsp": row["overlay"] || "" }];

        this.setState({
            view: this.EDIT,
            selectedPlaylistModel: row["slot_id"],
            selectedPlaylistName: row["playlist_name"],
            selectedPlaylistVariant: row["variant"],
            selectedPlaylistOverlay: row["overlay"],
            selectedPlaylistStatus: row["status"],
            availableVariants,
            availableOverlays,
            selectedPlaylistDeletable: row["deletable"],
            selectedPlaylistURL: row["slot_url"] || "",
            filters: filterState
        }, slotInfo ? this.setUrl : this.makeModelRequest);
    }

    private setUrl = () => {
        const hostUrl = getHostURL(this.props.customerConfig);

        const slotId = this.state.selectedPlaylistModel;
        const slotInfo = this.state.slotsInfo[slotId];
        const variant = this.state.selectedPlaylistVariant;
        const overlay = this.state.selectedPlaylistOverlay;

        let slotUrl = `${hostUrl}/slots/${slotId}/items?${variant}&${overlay}`;

        const filterState = this.state.filters;

        Object.keys(filterState).forEach((key: string) => {
            if (filterState[key].length > 0) {
                const values = filterState[key].map((filter) => {
                    return filter.value;
                });
                slotUrl += `&${key}=${values}`;
            }
        });
        
        if (slotInfo["userId"]) {
            slotUrl += "&userId={userId}";
        }

        if (slotInfo["seedId"]) {
            slotUrl += "&seedIds={seedId}";
        }

        this.setState({
            selectedPlaylistURL: slotUrl
        }, this.makeModelRequest);
    }

    private setPlaylistName = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            selectedPlaylistName: event.currentTarget.value,
            selectedPlaylistDeletable: true
        });
    }

    private setStatus = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            selectedPlaylistStatus: event.currentTarget.value
        });
    }

    private setModel = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const slotId = event.currentTarget.value;
        const slotInfo = this.state.slotsInfo[slotId];
        // Reset olderContent filter to default 15 when changing model type
        this.setState(state => ({
            selectedPlaylistModel: slotId,
            availableVariants: slotInfo["variants"],
            availableOverlays: slotInfo["overlays"],
            selectedPlaylistVariant: slotInfo["variants"][0]["qsp"],
            selectedPlaylistOverlay: slotInfo["overlays"][0]["qsp"],
            filters: {
                ...state.filters,
                olderContent: [{ value: "15", label: "15" }]
            }
        }), this.setUrl);
    }

    private setVariant = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            selectedPlaylistVariant: event.currentTarget.value
        }, this.setUrl);
    }

    private setOverlay = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            selectedPlaylistOverlay: event.currentTarget.value
        }, this.setUrl);
    }

    private setFilter(key: string, selected: any[]) {

        let selectedOptions: any = [];
        selected.forEach(item => {
            selectedOptions.push(item) ;
        });

        if (selectedOptions.length === 0) {
            selectedOptions = [];
        }

        const filterState = this.state.filters;
        filterState[key] = selectedOptions;

        this.setState({
            filters: filterState
        }, this.setUrl);
    }

    private setSeedId = (callbackValue: string, submit: boolean) => {
        this.setState({
            seedId: callbackValue
        }, this.makeModelRequest);
    }

    private setUserId = (callbackValue: string, submit: boolean) => {
        this.setState({
            userId: callbackValue
        }, this.makeModelRequest);
    }

    private copyURLToClipboard = (url: string) => {
        copy(this.formatUrl(url));
    }

    private copyDynamicURLToClipboard = () => {
        copy(this.formatUrl(this.state.selectedPlaylistURL));
    }

    private exportToCsv = () => {
        const availableModels = this.getSortedModels().map((model: Record<string, any>) => ({ ...model }));

        availableModels.forEach((model) => {
            model["created_by"] = model["audit_info"]?.["created_by"];
            model["date_created"] = model["audit_info"]?.["date_created"];
            model["updated_by"] = model["audit_info"]?.["updated_by"];
            model["date_updated"] = model["audit_info"]?.["date_updated"];

            const usage = this.getUsageForModel(model);
            model["1d_requests"] = usage?.["1d_total_requests"] || 0;
            model["7d_requests"] = usage?.["7d_total_requests"] || 0;
            model["30d_requests"] = usage?.["30d_total_requests"] || 0;
            model["used_in_7d"] = usage ? (usage["7d_total_requests"] || 0) > 0 : false;
            model["slot_type"] = this.getSlotTypeForModel(model);

            model["model_type"] = (model["model_type"] || "").toUpperCase();
            model["overlay"] = (model["overlay"] || "").includes("=") ? model["overlay"].split("=")[1] : model["overlay"];
            model["variant"] = (model["variant"] || "").includes("=") ? model["variant"].split("=")[1] : model["variant"];
            model["slot_url"] = this.formatUrl(model["slot_url"]);

            delete model["deletable"];
            delete model["audit_info"];
        });

        const timestamp = moment(new Date().toUTCString()).format(constants.YYMMDD_FORMAT);
        const csvConfig = mkConfig({ useKeysAsHeaders: true, filename: `available_models_${this.props.customerConfig.slug}_${timestamp}`});
        const csv = generateCsv(csvConfig)(availableModels);
        download(csvConfig)(csv);
    }

    private savePlaylist = () => {
        const modelBody: Record<string, any> = {
            "playlist_name": this.state.selectedPlaylistName,
            "model_type": this.state.slotsInfo[this.state.selectedPlaylistModel]?.type || "",
            "overlay": this.state.selectedPlaylistOverlay,
            "variant": this.state.selectedPlaylistVariant,
            "slot_url": this.state.selectedPlaylistURL,
            "status": this.state.selectedPlaylistStatus,
            "slot_id": this.state.selectedPlaylistModel,
            "deletable": this.state.selectedPlaylistDeletable,
            "audit_info": {
                "created_by": this.props.username,
                "updated_by": this.props.username
            }
        };

        Object.keys(this.state.filters).forEach((filterKey) => {
            if (this.state.filters[filterKey].length > 0) {
                modelBody[filterKey] = this.state.filters[filterKey];
            }
        });

        try {
            postModelDomainInformation(this.props.customerConfig, modelBody).then(() => {
                this.setState({
                    showSuccess: true
                });
            });

        } catch (error) {
            this.setState({
                errorMsg: "Error saving playlist"
            });
        }

    }

    private showDeleteModal(playlistName: string, modelType: string, modelId?: string) {
        this.setState({
            showDeleteModal: true,
            deletePlaylistName: playlistName,
            deleteModelType: modelType,
            deleteModelId: modelId || ""
        });
    }

    private handleDeleteModalClose() {
        this.setState({
            showDeleteModal: false,
            deletePlaylistName: "",
            deleteModelId: "",
            deleteModelType: ""
        }, () => {
            setTimeout(() => { this.reset(); }, 200);
        });
    }

    private async deletePlaylist() {
        if (this.state.deletePlaylistName) {
            try {
                // For dynamic models, use model_id; for classic models, use playlist_name
                const deleteIdentifier = this.state.deleteModelType === "dynamic"
                    ? this.state.deleteModelId
                    : this.state.deletePlaylistName;

                const response = await deleteModelTopic(this.props.customerConfig, deleteIdentifier);
                if (response.ok) {
                    this.setState({
                        errorMsg: "",
                        showDelete: true,
                        showDeleteModal: false
                    }, this.reset);
                } else if (response.status === 403) {
                    this.setState({
                        errorMsg: "Unable to delete item",
                        showDelete: true,
                        showDeleteModal: false
                    });
                } else {
                    this.setState({
                        errorMsg: response.text,
                        showDelete: true,
                        showDeleteModal: false
                    });
                }

            } catch (error) {
                this.setState({
                    errorMsg: "Error deleting playlist",
                    showDeleteModal: false
                });
            }
        }
    }

    private async makeModelRequest() {
        if (this.state.selectedPlaylistURL !== "") {
            try {
                let modelRequestURL = this.state.selectedPlaylistURL.replace("{userId}", this.state.userId).replace("{seedId}", this.state.seedId);
                modelRequestURL += "&ignore=portal";

                const itemsRequest = await makeSlotRequest(modelRequestURL, true);

                itemsRequest.json().then(async (responseJson) => {
                    this.setState({
                        items: responseJson["items"]
                    });
                });
            } catch (error) {
                //Whatever.
            }
        }
    }

    private formatUrl(url: string) {
        if (this.props.customerConfig.slug === "rtve") {
            if (url.indexOf("format=rtve") === -1) {
                const qmIndex = url.indexOf("?") + 1;
                const updatedUrl = url.substring(0, qmIndex) + "format=rtve&" + url.substring(qmIndex);
                return updatedUrl;
            }
        }

        return url;
    }

    private getUsageForModel(row: Record<string, any>): Record<string, any> | null {
        return this.state.usageByModel[row.model_id]
            || this.state.usageByModel[row.slot_id]
            || null;
    }

    private getSlotTypeForModel(row: Record<string, any>): string {
        if (row.model_type === "dynamic") {
            return "dynamic";
        }
        return this.state.slotsInfo[row.slot_id]?.type || row.model_type || "";
    }

    private getFilteredModels(): Array<Record<string, any>> {
        const { searchQuery } = this.state;
        if (!searchQuery.trim()) {
            return this.state.availableModelInfo;
        }

        const q = searchQuery.toLowerCase().trim();
        return this.state.availableModelInfo.filter((row: Record<string, any>) => {
            const fields = [
                row.playlist_name,
                row.model_type,
                row.owner,
                row.status,
                row.audit_info?.created_by,
                row.audit_info?.updated_by,
                row.slot_url
            ];

            return fields.some((f) => f && String(f).toLowerCase().includes(q));
        });
    }

    private getAdvancedFilteredModels(models: Array<Record<string, any>>): Array<Record<string, any>> {
        let result = models;

        if (this.state.filterStatus) {
            result = result.filter((row: Record<string, any>) => (row.status || "Live") === this.state.filterStatus);
        }

        if (this.state.filterOwner) {
            result = result.filter((row: Record<string, any>) => (row.owner || "24iQ") === this.state.filterOwner);
        }

        if (this.state.filterSlotType) {
            result = result.filter((row: Record<string, any>) => this.getSlotTypeForModel(row) === this.state.filterSlotType);
        }

        if (this.state.filterUsedIn7d === "yes") {
            result = result.filter((row: Record<string, any>) => {
                const usage = this.getUsageForModel(row);
                return usage && (usage["7d_total_requests"] || 0) > 0;
            });
        } else if (this.state.filterUsedIn7d === "no") {
            result = result.filter((row: Record<string, any>) => {
                const usage = this.getUsageForModel(row);
                return !usage || (usage["7d_total_requests"] || 0) === 0;
            });
        }

        const activeConditionalGroups = this.state.conditionalFilterGroups.filter((group) => group.topic);

        if (activeConditionalGroups.length > 0) {
            result = result.filter((row: Record<string, any>) => {
                const conditionalFilters = row.conditional_filters;
                if (!conditionalFilters || !Array.isArray(conditionalFilters.groups)) {
                    return false;
                }

                return activeConditionalGroups.every((selectedGroup) => {
                    const requestedValues = selectedGroup.values.map((value) => value.trim().toLowerCase()).filter(Boolean);

                    return conditionalFilters.groups.some((group: Record<string, any>) =>
                        (group.conditions || []).some((condition: Record<string, any>) => {
                            if (condition.topic !== selectedGroup.topic) {
                                return false;
                            }

                            if (requestedValues.length === 0) {
                                return true;
                            }

                            const conditionValues = (condition.values || [])
                                .join(",")
                                .split(",")
                                .map((value: string) => value.trim().toLowerCase())
                                .filter(Boolean);

                            return requestedValues.some((value) => conditionValues.includes(value));
                        })
                    );
                });
            });
        }

        return result;
    }

    private getSortedModels(): Array<Record<string, any>> {
        const baseModels = this.getAdvancedFilteredModels(this.getFilteredModels());
        const { sortBy, sortOrder } = this.state;

        return [...baseModels].sort((a: Record<string, any>, b: Record<string, any>) => {
            let valA: any;
            let valB: any;
            let valueType: "string" | "number" = "string";

            switch (sortBy) {
                case "name":
                    valA = a.playlist_name || "";
                    valB = b.playlist_name || "";
                    break;
                case "owner":
                    valA = a.owner || "24iQ";
                    valB = b.owner || "24iQ";
                    break;
                case "status":
                    valA = a.status || "";
                    valB = b.status || "";
                    break;
                case "slotType":
                    valA = this.getSlotTypeForModel(a);
                    valB = this.getSlotTypeForModel(b);
                    break;
                case "lastUpdated":
                    valA = Date.parse(a.audit_info?.date_updated || "") || 0;
                    valB = Date.parse(b.audit_info?.date_updated || "") || 0;
                    valueType = "number";
                    break;
                case "createdAt":
                    valA = Date.parse(a.audit_info?.date_created || "") || 0;
                    valB = Date.parse(b.audit_info?.date_created || "") || 0;
                    valueType = "number";
                    break;
                case "usage_1d":
                case "usage_7d":
                case "usage_30d": {
                    const field = `${sortBy.replace("usage_", "")}_total_requests`;
                    const usageA = this.getUsageForModel(a);
                    const usageB = this.getUsageForModel(b);
                    valA = Number(usageA?.[field] || 0);
                    valB = Number(usageB?.[field] || 0);
                    valueType = "number";
                    break;
                }
                default:
                    valA = a.model_type || "";
                    valB = b.model_type || "";
            }

            let compare = 0;
            if (valueType === "number") {
                compare = valA - valB;
            } else {
                compare = String(valA).localeCompare(String(valB), undefined, {
                    sensitivity: "base",
                    numeric: true
                });
            }

            if (compare === 0) {
                compare = String(a.playlist_name || "").localeCompare(String(b.playlist_name || ""), undefined, {
                    sensitivity: "base",
                    numeric: true
                });
            }

            return sortOrder === "desc" ? -compare : compare;
        });
    }

    private getDistinctValues(field: "owner" | "model_type" | "status" | "slot_type"): string[] {
        const values = new Set<string>();
        this.state.availableModelInfo.forEach((row: Record<string, any>) => {
            let value = "";
            if (field === "owner") {
                value = row.owner || "24iQ";
            } else if (field === "slot_type") {
                value = this.getSlotTypeForModel(row);
            } else {
                value = row[field] || "";
            }

            if (value) {
                values.add(value);
            }
        });

        return [...values].sort((a, b) => a.localeCompare(b));
    }

    private getDistinctConditionalTopics(): string[] {
        const topics = new Set<string>();
        this.state.availableModelInfo.forEach((row: Record<string, any>) => {
            const conditionalFilters = row.conditional_filters;
            if (conditionalFilters && Array.isArray(conditionalFilters.groups)) {
                conditionalFilters.groups.forEach((group: Record<string, any>) => {
                    (group.conditions || []).forEach((condition: Record<string, any>) => {
                        if (condition.topic) {
                            topics.add(condition.topic);
                        }
                    });
                });
            }
        });

        return [...topics].sort((a, b) => a.localeCompare(b));
    }

    private getDistinctConditionalValues(topic: string): string[] {
        const values = new Set<string>();
        this.state.availableModelInfo.forEach((row: Record<string, any>) => {
            const conditionalFilters = row.conditional_filters;
            if (conditionalFilters && Array.isArray(conditionalFilters.groups)) {
                conditionalFilters.groups.forEach((group: Record<string, any>) => {
                    (group.conditions || []).forEach((condition: Record<string, any>) => {
                        if (condition.topic === topic) {
                            (condition.values || []).forEach((value: string) => {
                                value.split(",").forEach((part: string) => {
                                    if (part.trim()) {
                                        values.add(part.trim());
                                    }
                                });
                            });
                        }
                    });
                });
            }
        });

        return [...values].sort((a, b) => a.localeCompare(b));
    }

    private addConditionalFilterGroup = (): void => {
        this.setState((prev) => ({
            conditionalFilterGroups: [...prev.conditionalFilterGroups, { topic: "", values: [] }]
        }));
    }

    private removeConditionalFilterGroup = (index: number): void => {
        this.setState((prev) => ({
            conditionalFilterGroups: prev.conditionalFilterGroups.filter((_, i) => i !== index)
        }));
    }

    private updateConditionalFilterTopic = (index: number, topic: string): void => {
        this.setState((prev) => ({
            conditionalFilterGroups: prev.conditionalFilterGroups.map((group, i) =>
                i === index ? { topic, values: [] } : group
            )
        }));
    }

    private updateConditionalFilterValues = (index: number, values: string[]): void => {
        this.setState((prev) => ({
            conditionalFilterGroups: prev.conditionalFilterGroups.map((group, i) =>
                i === index ? { ...group, values } : group
            )
        }));
    }

    private renderModelTableHeader(): ReactNode {
        return (
            <ModelTableHeader>
                <ModelTableHeaderBody>
                    <div>Name</div>
                    <div>Type</div>
                    <div>Slot</div>
                    <div>Owner</div>
                    <div>Updated By</div>
                    <div>Created</div>
                    <div>Updated</div>
                    <div>Status</div>
                    <div>Active</div>
                    <div>7d Request Count</div>
                </ModelTableHeaderBody>
            </ModelTableHeader>
        );
    }

    private renderModelRow(row: Record<string, any>, isReadOnly: boolean): ReactNode {
        const formattedUrl = this.formatUrl(row.slot_url);
        const usage = this.getUsageForModel(row);
        const usedInLast7d = usage ? (usage["7d_total_requests"] || 0) > 0 : false;
        const slotInfo = this.state.slotsInfo[row.slot_id];
        const slotName = slotInfo?.name || row.slot_id || "";

        return (
            <ModelRow key={row.model_id || row.playlist_name}>
                <ModelGrid>
                    <ModelGridAccent $accentcolor={getModelTypeColor(row.model_type)} />
                    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <ModelGridBody>
                            <ModelGridCell><ModelNamePill title={row.playlist_name}>{row.playlist_name}</ModelNamePill></ModelGridCell>
                            <ModelGridCell style={{ color: getModelTypeColor(row.model_type) }}>{(row.model_type || "").replaceAll("_", " ")}</ModelGridCell>
                            <ModelGridCell title={row.slot_id}>{slotName || "—"}</ModelGridCell>
                            <ModelGridCell>{row.owner || "24iQ"}</ModelGridCell>
                            <ModelGridCell>{row.audit_info?.updated_by || "—"}</ModelGridCell>
                            <ModelGridCell>{row.audit_info?.date_created ? moment(row.audit_info.date_created).format("DD MMM YY") : "—"}</ModelGridCell>
                            <ModelGridCell>{row.audit_info?.date_updated ? moment(row.audit_info.date_updated).format("DD MMM YY") : "—"}</ModelGridCell>
                            <ModelGridCell>{row.status || "Live"}</ModelGridCell>
                            <ModelGridCell style={{ color: usedInLast7d ? Branding.twentyfouriLime : "#ff6b6b" }}>{usedInLast7d ? "Yes" : "No"}</ModelGridCell>
                            <ModelGridCell>{usage ? (usage["7d_total_requests"] || 0) : "—"}</ModelGridCell>
                        </ModelGridBody>
                        <ModelUrlRow title={formattedUrl}>{formattedUrl}</ModelUrlRow>
                    </div>
                    <ModelGridActions>
                        {row.notes && (
                            <OverlayTrigger placement="auto" overlay={<Tooltip id={(row.model_id || row.playlist_name) + "notes-tooltip"}>{row.notes}</Tooltip>}>
                                <span style={{ cursor: "default", color: "#fff", lineHeight: 1 }}><InfoIcon size={15} /></span>
                            </OverlayTrigger>
                        )}

                        <OverlayTrigger placement="auto" overlay={<Tooltip id={(row.model_id || row.playlist_name) + "edit-tooltip"}>Edit</Tooltip>}>
                            <Button size="sm" onClick={() => { this.switchToEdit(row); }}><EditIcon size={16} /></Button>
                        </OverlayTrigger>

                        <OverlayTrigger placement="auto" overlay={<Tooltip id={(row.model_id || row.playlist_name) + "url-tooltip"}>Copy URL</Tooltip>}>
                            <Button size="sm" variant="secondary" onClick={() => { this.copyURLToClipboard(row.slot_url); }}><CopyIcon size={14} /></Button>
                        </OverlayTrigger>

                        {!isReadOnly && (
                            <OverlayTrigger placement="auto" overlay={row.deletable ? <Tooltip id={(row.model_id || row.playlist_name) + "delete-tooltip"}>Delete</Tooltip> : <div />}>
                                <Button size="sm" variant={row.deletable ? "danger" : "outline-danger"} disabled={!row.deletable} onClick={() => { this.showDeleteModal(row.playlist_name, row.model_type, row.model_id); }}>
                                    <DeleteIcon size={16} />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </ModelGridActions>
                </ModelGrid>
            </ModelRow>
        );
    }

    private renderContent(modelType: string) {
        const isReadOnly = isReadOnlyUser(this.props.groups, this.props.customerConfig);
        const isTheFilterFullUser = this.props.groups.includes(constants.THE_FILTER_FULL_AUTH);
        const sortedModels = this.getSortedModels();
        const conditionalTopics = this.getDistinctConditionalTopics();
        const activeFilterCount = [
            this.state.filterStatus,
            this.state.filterOwner,
            this.state.filterSlotType,
            this.state.filterUsedIn7d
        ].filter(Boolean).length + this.state.conditionalFilterGroups.filter((g) => g.topic).length;
        const additionalFilters = Object.keys(this.state.filters).filter((key) => {
            return key !== "genre" && key !== "typename" && key !== "excludeGenre" && key !== "audience";
        });

        switch (this.state.view) {
            case this.OVERVIEW:
                return (
                    <div>
                        <>
                        <StickyToolbar>
                        <Row className="align-items-center" style={{ marginBottom: "8px", rowGap: "8px" }}>
                            <Col xs="12" md="4" style={{ paddingRight: "6px" }}>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Search models..."
                                    value={this.state.searchQuery}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ searchQuery: event.target.value })}
                                />
                            </Col>
                            <Col xs="12" md="8" className="d-flex justify-content-end" style={{ paddingLeft: "6px" }}>
                                <div className="d-flex flex-column flex-sm-row w-100 justify-content-sm-end" style={{ gap: "8px" }}>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="align-self-stretch align-self-sm-auto"
                                    style={{ minWidth: "132px" }}
                                    onClick={() => this.setState((state) => ({ showAdvancedFilters: !state.showAdvancedFilters }))}
                                >
                                    Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                                </Button>
                                <Form.Control
                                    as="select"
                                    size="sm"
                                    style={{ width: "100%", maxWidth: "280px" }}
                                    value={this.state.sortBy}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const nextSortBy = event.target.value;
                                        const descByDefaultSorts = ["lastUpdated", "createdAt", "usage_1d", "usage_7d", "usage_30d"];
                                        this.setState({
                                            sortBy: nextSortBy,
                                            sortOrder: descByDefaultSorts.includes(nextSortBy) ? "desc" : "asc"
                                        });
                                    }}
                                >
                                    <option value="model_type">Sort: Model Type</option>
                                    <option value="name">Sort: Name</option>
                                    <option value="owner">Sort: Owner</option>
                                    <option value="status">Sort: Status</option>
                                    <option value="slotType">Sort: Slot Type</option>
                                    <option value="lastUpdated">Sort: Last Updated</option>
                                    <option value="createdAt">Sort: Date Created</option>
                                    <option value="usage_1d">Sort: Usage (1d)</option>
                                    <option value="usage_7d">Sort: Usage (7d)</option>
                                    <option value="usage_30d">Sort: Usage (30d)</option>
                                </Form.Control>
                                <Button size="sm" variant="outline-secondary" className="align-self-stretch align-self-sm-auto" onClick={() => this.setState((state) => ({ sortOrder: state.sortOrder === "asc" ? "desc" : "asc" }))}>
                                    {this.state.sortOrder === "asc" ? "Asc" : "Desc"}
                                </Button>
                                </div>
                            </Col>
                        </Row>
                        <Collapse in={this.state.showAdvancedFilters}>
                            <div style={{ padding: "8px", marginBottom: "10px", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px" }}>
                                <Row>
                                    <Col md="3">
                                        <Form.Control as="select" size="sm" value={this.state.filterStatus} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ filterStatus: event.target.value })}>
                                            <option value="">All Statuses</option>
                                            {this.availableStatus
                                                .filter((status) => status !== "24iQ Only" || isTheFilterFullUser)
                                                .map((status) => <option key={status} value={status}>{status}</option>)}
                                        </Form.Control>
                                    </Col>
                                    <Col md="3">
                                        <Form.Control as="select" size="sm" value={this.state.filterUsedIn7d} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ filterUsedIn7d: event.target.value })}>
                                            <option value="">Active: All</option>
                                            <option value="yes">Active: Yes</option>
                                            <option value="no">Active: No</option>
                                        </Form.Control>
                                    </Col>
                                    <Col md="3">
                                        <Form.Control as="select" size="sm" value={this.state.filterSlotType} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ filterSlotType: event.target.value })}>
                                            <option value="">All Slot Types</option>
                                            {this.getDistinctValues("slot_type").map((value) => <option key={value} value={value}>{value}</option>)}
                                        </Form.Control>
                                    </Col>
                                    <Col md="3">
                                        <Form.Control as="select" size="sm" value={this.state.filterOwner} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ filterOwner: event.target.value })}>
                                            <option value="">All Owners</option>
                                            {this.getDistinctValues("owner").map((value) => <option key={value} value={value}>{value}</option>)}
                                        </Form.Control>
                                    </Col>
                                </Row>
                                {this.state.conditionalFilterGroups.map((group, index) => {
                                    const conditionalValues = group.topic ? this.getDistinctConditionalValues(group.topic) : [];
                                    return (
                                        <Row key={`conditional-group-${index}`} style={{ marginTop: "6px" }}>
                                            <Col md="4">
                                                <Form.Control
                                                    as="select"
                                                    size="sm"
                                                    value={group.topic}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.updateConditionalFilterTopic(index, event.target.value)}
                                                >
                                                    <option value="">Filter Topic</option>
                                                    {conditionalTopics.map((value) => <option key={value} value={value}>{value}</option>)}
                                                </Form.Control>
                                            </Col>
                                            <Col md="6">
                                                <Typeahead
                                                    id={`conditional-values-${index}`}
                                                    multiple
                                                    clearButton
                                                    options={conditionalValues}
                                                    selected={group.values}
                                                    placeholder={group.topic ? "Filter Values" : "Select a topic first"}
                                                    disabled={!group.topic}
                                                    onChange={(selected: any[]) => this.updateConditionalFilterValues(index, selected as string[])}
                                                />
                                            </Col>
                                            <Col md="2" className="d-flex justify-content-end">
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => this.removeConditionalFilterGroup(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                    );
                                })}
                                <Row style={{ marginTop: "6px" }}>
                                    <Col md="6" className="d-flex">
                                        <Button size="sm" variant="outline-secondary" onClick={this.addConditionalFilterGroup}>
                                            Add Topic + Values Filter
                                        </Button>
                                    </Col>
                                    <Col md="6" className="d-flex justify-content-end">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => this.setState({
                                                filterStatus: "",
                                                filterOwner: "",
                                                filterSlotType: "",
                                                filterUsedIn7d: "",
                                                conditionalFilterGroups: []
                                            })}
                                        >
                                            Clear Filters
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </Collapse>
                        {this.renderModelTableHeader()}
                        </StickyToolbar>
                        <div key={`sort-${this.state.sortBy}-${this.state.sortOrder}`}>
                        {this.state.sortBy === "model_type" ? (
                            Object.entries(
                                sortedModels.reduce((acc: Record<string, any[]>, row: Record<string, any>) => {
                                    (acc[row.model_type] = acc[row.model_type] || []).push(row);
                                    return acc;
                                }, {})
                            ).map(([groupType, rows]) => (
                                <div key={groupType}>
                                    <ModelGroupHeader $accentcolor={getModelTypeColor(groupType)}>
                                        {groupType.replaceAll("_", " ")}
                                    </ModelGroupHeader>
                                    {rows.map((row: Record<string, any>) => this.renderModelRow(row, isReadOnly))}
                                </div>
                            ))
                        ) : (
                            sortedModels.map((row: Record<string, any>) => this.renderModelRow(row, isReadOnly))
                        )}
                        </div>
                        <Row className="d-flex justify-content-end" style={{ marginTop: "16px" }}>
                            <Button onClick={this.exportToCsv}>Export to CSV</Button>
                            {!isReadOnly && (
                                <Button style={{ marginLeft: "5px" }} onClick={this.switchToNew}>
                                    Add new model playlist
                                </Button>
                            )}
                        </Row>
                        </>
                    </div>
                );
            case this.NEW:
            case this.EDIT:
                return <>
                    <Row style={{paddingTop: "20px"}}>
                        <Col md="8">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Playlist Name</InputGroup.Text>
                                </InputGroup.Prepend>
                                <FieldValue
                                    id="playlistNameInput"
                                    type="string"
                                    placeholder="Playlist Name"
                                    value={this.state.selectedPlaylistName}
                                    onChange={this.setPlaylistName}
                                />
                                <InputGroup.Append>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="deletable-toggle">
                                            {this.state.selectedPlaylistDeletable ? "Unlocked: can be deleted" : "Locked: cannot be deleted"}
                                        </Tooltip>}
                                    >
                                        <Button
                                            variant={this.state.selectedPlaylistDeletable ? "outline-success" : "outline-secondary"}
                                            onClick={this.toggleSelectedPlaylistDeletable}
                                        >
                                            {this.state.selectedPlaylistDeletable ? <UnlockIcon /> : <LockIcon />}
                                        </Button>
                                    </OverlayTrigger>
                                </InputGroup.Append>
                            </InputGroup>
                        </Col>
                        <Col md="4">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Overlay</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    as="select"
                                    id="dropdown-overlay"
                                    title="Overlay"
                                    value={this.state.selectedPlaylistOverlay}
                                    onChange={this.setOverlay}
                                >
                                    {Object.values(this.state.availableOverlays).map((overlay: Record<string, any>) => {
                                        return <option value={overlay["qsp"]}>{overlay["name"]}</option>;
                                    })}
                                </Form.Control>
                            </InputGroup>
                        </Col>
                    </Row>
                    <Row style={{paddingTop: "10px"}}>
                        <Col md="6">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Model Type</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    as="select"
                                    id="dropdown-model"
                                    title="Model Type"
                                    value={this.state.selectedPlaylistModel}
                                    onChange={this.setModel}
                                >
                                    {Object.values(this.state.slotsInfo).map((slot: Record<string, any>) => {
                                        return <option key={slot["id"]} value={slot["id"]}>{slot["name"]}</option>;
                                    })}
                                </Form.Control>
                            </InputGroup>
                        </Col>
                        <Col md="6">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Variant</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    as="select"
                                    id="dropdown-variant"
                                    title="Variant"
                                    value={this.state.selectedPlaylistVariant}
                                    onChange={this.setVariant}
                                >
                                    {Object.values(this.state.availableVariants).map((variant: Record<string, any>) => {
                                        return <option key={variant["qsp"]} value={variant["qsp"]}>{variant["name"]}</option>;
                                    })}
                                </Form.Control>
                            </InputGroup>
                        </Col>
                        <Col md="6">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Status</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    as="select"
                                    id="dropdown-status"
                                    title="Status"
                                    value={this.state.selectedPlaylistStatus}
                                    onChange={this.setStatus}
                                >
                                    {this.availableStatus
                                        .filter((status) => status !== "24iQ Only" || isTheFilterFullUser)
                                        .map((status) => {
                                            return <option key={status} value={status}>{status}</option>;
                                        })}
                                </Form.Control>
                            </InputGroup>
                        </Col>
                    </Row>
                    {this.filterableModels.indexOf(modelType) > -1 &&
                    <>
                        <Row style={{paddingTop: "10px"}}>
                            {this.state.availableFilters["genre"] && <Col md="4">
                                <InputGroup>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>{this.state.availableFilters["genre"].label} Filter</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Typeahead
                                        id="genre-typeahead"
                                        multiple={this.state.availableFilters["genre"]["cardinality"] === "multiple"}
                                        options={this.state.availableFilters["genre"].values.map((genre: Record<string, any>) => {
                                            return {"value": genre.id, "label": genre.label};
                                        })}
                                        selected={Object.values(this.state.filters["genre"]).map((genre: Record<string, any>) => {
                                            return {"value": genre.value, "label": genre.label};
                                        })}
                                        placeholder={`Select ${this.state.availableFilters["genre"].label} filter...`}
                                        onChange={(evt) => { this.setFilter("genre", evt);}}
                                        clearButton
                                    />
                                </InputGroup>
                            </Col>}
                            {this.state.availableFilters["typename"] && <Col md="4">
                                <InputGroup>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>{this.state.availableFilters["typename"].label} Filter</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Typeahead
                                        id="typename-typeahead"
                                        multiple={this.state.availableFilters["typename"]["cardinality"] === "multiple"}
                                        options={this.state.availableFilters["typename"].values.map((typename: Record<string, any>) => {
                                            return {"value": typename.id, "label": typename.label};
                                        })}
                                        selected={Object.values(this.state.filters["typename"]).map((typename:  Record<string, any>) => {
                                            return {"value": typename.value, "label": typename.label};
                                        })}
                                        placeholder={`Select ${this.state.availableFilters["typename"].label} filter...`}
                                        onChange={(evt) => { this.setFilter("typename", evt);}}
                                        clearButton
                                    />
                                </InputGroup>
                            </Col>}
                            {this.state.availableFilters["excludeGenre"] && <Col md="4">
                                <InputGroup>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>{this.state.availableFilters["excludeGenre"].label} Filter</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Typeahead
                                        id="excludeGenre-typeahead"
                                        multiple={this.state.availableFilters["excludeGenre"]["cardinality"] === "multiple"}
                                        options={this.state.availableFilters["excludeGenre"].values.map((genre: Record<string, any>) => {
                                            return {"value": genre.id, "label": genre.label};
                                        })}
                                        selected={Object.values(this.state.filters["excludeGenre"]).map((genre: Record<string, any>) => {
                                            return {"value": genre.value, "label": genre.label};
                                        })}
                                        placeholder={`Select ${this.state.availableFilters["excludeGenre"].label} filter...`}
                                        onChange={(evt) => { this.setFilter("excludeGenre", evt);}}
                                        clearButton
                                    />
                                </InputGroup>
                            </Col>}
                        </Row>
                        {additionalFilters.length > 0 && additionalFilters.length <= 3 && <Row style={{paddingTop: "10px"}}>
                            {additionalFilters.slice(0,3).map((additionalFilter) => {
                                const filterObj = this.state.availableFilters[additionalFilter];
                                if (!filterObj) return null;
                                return <Col key={additionalFilter}>
                                    <InputGroup>
                                        <InputGroup.Prepend>
                                            <InputGroup.Text>{filterObj.label} Filter</InputGroup.Text>
                                        </InputGroup.Prepend>
                                        <Typeahead
                                            id={`${additionalFilter}-typeahead`}
                                            multiple={filterObj["cardinality"] === "multiple"}
                                            align="left"
                                            options={Array.isArray(filterObj.values) ? filterObj.values.map((item: Record<string, any>) => ({ value: item.id, label: item.label })) : []}
                                            selected={Array.isArray(this.state.filters[additionalFilter]) ? this.state.filters[additionalFilter].map((item: Record<string, any>) => ({ value: item.value, label: item.label })) : []}
                                            placeholder={`Select ${filterObj.label} filter...`}
                                            onChange={(evt) => { this.setFilter(additionalFilter, evt);}}
                                            clearButton
                                        />
                                    </InputGroup>
                                </Col>;
                            })}
                        </Row>}
                        {additionalFilters.length > 3 && additionalFilters.length <= 6 && <Row style={{paddingTop: "10px"}}>
                            {additionalFilters.slice(3,6).map((additionalFilter) => {
                                const filterObj = this.state.availableFilters[additionalFilter];
                                if (!filterObj) return null;
                                return <Col key={additionalFilter}>
                                    <InputGroup>
                                        <InputGroup.Prepend>
                                            <InputGroup.Text>{filterObj.label} Filter</InputGroup.Text>
                                        </InputGroup.Prepend>
                                        <Typeahead
                                            id={`${additionalFilter}-typeahead`}
                                            multiple={filterObj["cardinality"] === "multiple"}
                                            align="left"
                                            options={Array.isArray(filterObj.values) ? filterObj.values.map((item: Record<string, any>) => ({ value: item.id, label: item.label })) : []}
                                            selected={Array.isArray(this.state.filters[additionalFilter]) ? this.state.filters[additionalFilter].map((item: Record<string, any>) => ({ value: item.value, label: item.label })) : []}
                                            placeholder={`Select ${filterObj.label} filter...`}
                                            onChange={(evt) => { this.setFilter(additionalFilter, evt);}}
                                            clearButton
                                        />
                                    </InputGroup>
                                </Col>;
                            })}
                        </Row>}
                    </>}
                    {this.state.availableFilters["audience"] && <Row style={{paddingTop: "10px"}}>
                        <Col md="4">
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>{this.state.availableFilters["audience"].label} Filter</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Typeahead
                                    id="audience-typeahead"
                                    multiple={this.state.availableFilters["audience"]["cardinality"] === "multiple"}
                                    options={this.state.availableFilters["audience"].values.map((audience: Record<string, any>) => {
                                        return {"value": audience.id, "label": audience.label};
                                    })}
                                    selected={Object.values(this.state.filters["audience"]).map((audience:  Record<string, any>) => {
                                        return {"value": audience.value, "label": audience.label};
                                    })}
                                    placeholder={`Select ${this.state.availableFilters["audience"].label} filter...`}
                                    onChange={(evt) => { this.setFilter("audience", evt);}}
                                    clearButton
                                />
                            </InputGroup>
                        </Col>
                        <Col md="4">
                        {/* Range filter for Older Content (olderContent) for specific model types */}
                        { ["mlt", "mlt_audio", "rfy", "rfy_audio", "byw", "byw_audio"].includes(modelType) && (
                            <Col md="12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                                <InputGroup>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>Older Content  <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ minWidth: 32, textAlign: "center" }}>
                                            {this.state.filters["olderContent"]?.[0]?.value ?? 15}
                                        </span>
                                    <Form.Control
                                        type="range"
                                        min={0}
                                        max={50}
                                        step={1}
                                        value={this.state.filters["olderContent"]?.[0]?.value ?? 15}
                                        style={{ flex: 1, maxWidth: 300 }}
                                        onChange={e => {
                                            // Update local filter state immediately; full filter update is triggered onMouseUp
                                            this.setState(state => ({
                                                filters: {
                                                    ...state.filters,
                                                    olderContent: [{ value: e.target.value, label: e.target.value }]
                                                }
                                            }));
                                        }}
                                        onMouseUp={(e: React.MouseEvent<HTMLInputElement>) => {
                                            const value = (e.target as HTMLInputElement).value;
                                            this.setFilter("olderContent", [{ value, label: value }]);
                                        }}
                                    />
                                </div>
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                </InputGroup>
                            </Col>
                        )}
                        </Col>
                    </Row>}
                    <Row style={{paddingTop: "10px"}}>
                        <Col>
                            <InputGroup>
                                <InputGroup.Prepend>
                                    <InputGroup.Text>Slot URL</InputGroup.Text>
                                </InputGroup.Prepend>
                                <FieldValue
                                    id="slotUrlOutput"
                                    type="string"
                                    placeholder="Slot URL"
                                    readOnly
                                    value={this.formatUrl(this.state.selectedPlaylistURL)}
                                />
                                <InputGroup.Append>
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={<Tooltip id="search-tooltip">Copy URL</Tooltip>}>
                                        <Button variant="primary" type="button" onClick={this.copyDynamicURLToClipboard}>
                                            <CopyIcon size={18}/>
                                        </Button>
                                    </OverlayTrigger>
                                </InputGroup.Append>
                            </InputGroup>
                        </Col>
                    </Row>
                    <Row style={{paddingTop: "10px"}}>
                        <Col>
                            <RandomField
                                customer={this.props.customerConfig}
                                type={constants.USER}
                                includeIcon
                                initialValue={this.state.userId}
                                onChangeCallback={this.setUserId}
                            />
                        </Col>
                        <Col>
                            <RandomField
                                customer={this.props.customerConfig}
                                type={constants.SEED}
                                includeIcon
                                initialValue={this.state.seedId}
                                onChangeCallback={this.setSeedId}
                                showName
                            />
                        </Col>
                    </Row>
                    <CarouselRow style={{paddingTop: "10px"}}>
                        <CentralCol>
                            <h5 className="mx-auto">Results Preview</h5>
                            {this.renderPreview()}
                        </CentralCol>
                    </CarouselRow>
                    <Row style={{paddingTop: "10px"}} className="d-flex justify-content-end">
                        <Col className="d-flex justify-content-end">
                            {!isReadOnly && <Button onClick={this.savePlaylist}>Save Playlist</Button>}
                            <Button variant="secondary" style={{marginLeft: "5px"}} onClick={this.switchToOverview}>Return to Overview</Button>
                        </Col>
                    </Row>
                </>;
            default:
                return null;
        }
    }


    private renderPreview(): ReactNode {
        if (!this.state.items) {
            return null;
        }

        return (
            <MLTRow>
                <SelectedEntityCol md>
                    <MetadataCarousel
                        key={JSON.stringify(this.state.items)}
                        items={this.state.items}
                        customer={this.props.customerConfig}
                        username={this.props.username}
                        groups={this.props.groups}
                        lessItems
                        hasEntity={true}
                        hideMissingText
                        itemText
                    />
                </SelectedEntityCol>
            </MLTRow>
        );
    }


    public render(): ReactNode {
        let modelType = "";
        if (this.state.selectedPlaylistModel) {
            modelType = this.state.slotsInfo[this.state.selectedPlaylistModel]?.type || "";
        }

        return (
            <>
                <ParametersAlert
                    variant='success'
                    show={this.state.showSuccess}
                    onClose={this.closeSuccess}
                    dismissible
                >
                    Model playlist saved
                </ParametersAlert>
                <Modal
                    show={this.state.showDeleteModal}
                    onHide={()=> { this.handleDeleteModalClose(); }}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Model?</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <p>
                            Do you want to delete model with name: <b>{this.state.deletePlaylistName}</b>?
                            <br/>
                            <b>This cannot be undone</b>
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {this.handleDeleteModalClose();}}>Cancel</Button>
                        <Button variant="danger" onClick={() => {this.deletePlaylist();}}>Delete</Button>
                    </Modal.Footer>
                </Modal>
                <ModelTypeModal
                    show={this.state.showModelTypeModal}
                    onClose={this.closeModelTypeModal}
                    onSelectClassic={this.handleClassicModelSelection}
                    onSelectDynamic={this.handleDynamicModelSelection}
                />
                <ParametersAlert
                    variant={this.state.errorMsg === "" ? "success" : "danger"}
                    show={this.state.showDelete}
                    onClose={this.closeDelete}
                    dismissible
                >
                    {this.state.errorMsg === "" ? "Model playlist deleted" : this.state.errorMsg}
                </ParametersAlert>
                <Container className="mw-100" key="model-manager" style={{paddingTop: "15px"}}>
                    <Row className="d-flex justify-content-center">
                        <Col md="11">
                            <h4>Model Manager - {this.state.view}</h4>
                            {this.renderContent(modelType)}
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }
}

export default withRouter(ModelManager);